import React, { useCallback, useEffect, useState, lazy } from 'react';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
const accreditations = [{
  logo: 'https://cdn.magicpatterns.com/uploads/1UjdKxa8iRZZRQpqZEdYa7/GMC.png',
  label: 'General Medical Council',
  detail: 'All panel experts are GMC-registered and hold a current licence to practise.',
  alt: 'General Medical Council logo — all UK Medicolegal panel experts are GMC registered'
}, {
  logo: 'https://cdn.magicpatterns.com/uploads/9uhGWLUkFVJvbmpaUD8EFX/medical_school_council.png',
  label: 'Medical Schools Council',
  detail: 'Recognised by the Medical Schools Council — upholding UK clinical standards.',
  alt: 'Medical Schools Council logo — recognised for upholding UK clinical standards'
}, {
  logo: 'https://cdn.magicpatterns.com/uploads/brp7RQSAcdspsBC293E8GE/ICO.png',
  label: 'ICO Registered',
  detail: "Registered with the Information Commissioner's Office for data protection compliance.",
  alt: "Information Commissioner's Office logo — ICO registered for data protection"
}, {
  logo: 'https://cdn.magicpatterns.com/uploads/5v3Sk4Uszm3DXHmTxGduth/Medco.png',
  label: 'MedCo Registered',
  detail: 'Registered with MedCo for medico-legal reporting in personal injury claims.',
  alt: 'MedCo logo — registered for medico-legal reporting in personal injury'
}, {
  logo: 'https://cdn.magicpatterns.com/uploads/irizXbZRLh7ci6fJcV5PiQ/RCPsych.png',
  label: 'Royal College of Psychiatrists',
  detail: 'Member of the Royal College of Psychiatrists — trusted UK psychiatric authority.',
  alt: 'Royal College of Psychiatrists logo — member of the trusted UK psychiatric authority'
}, {
  logo: 'https://cdn.magicpatterns.com/uploads/onGvtXH5gAbUi97QoMQjRQ/Bond_Solon.jpg',
  label: 'Bond Solon Accredited',
  detail: 'Certified Expert Witness — Bond Solon accredited for expert witness training.',
  alt: 'Bond Solon Expert Witness logo — certified expert witness accreditation'
}];
const highlights = [{
  value: '15,000+',
  label: 'Reports delivered since 2015'
}, {
  value: '1,000+',
  label: 'Vetted panel experts UK-wide'
}, {
  value: '98%',
  label: 'Reports accepted without challenge'
}, {
  value: '4.8/5',
  label: 'Average client satisfaction score'
}];
// Number of pages (3 items per page)
const ITEMS_PER_PAGE = 3;
const totalPages = Math.ceil(accreditations.length / ITEMS_PER_PAGE);
export function Credentials() {
  const [currentPage, setCurrentPage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const goToNext = useCallback(() => {
    setCurrentPage(prev => (prev + 1) % totalPages);
  }, []);
  const goToPrev = useCallback(() => {
    setCurrentPage(prev => (prev - 1 + totalPages) % totalPages);
  }, []);
  // Auto-advance every 3 seconds
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(goToNext, 3000);
    return () => clearInterval(interval);
  }, [isPaused, goToNext]);
  const visibleItems = accreditations.slice(currentPage * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE);
  return <section className="py-10 bg-warm-100" aria-label="Our accreditations and credentials" data-id="element-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-33">
        {/* Accreditations & Memberships */}
        <div className="text-center max-w-2xl mx-auto mb-6" data-id="element-34">
          <h2 className="text-3xl md:text-4xl font-bold text-ink-900 tracking-tight leading-tight mb-2 mt-0" data-id="element-35">
            Our Accreditations & Memberships
          </h2>
          <p className="text-lg text-ink-500 leading-relaxed" data-id="element-36">
            Accredited, insured, and independently audited — our credentials
            give you confidence in every instruction.
          </p>
        </div>

        {/* Carousel — dark navy background */}
        <div className="bg-[#1A2B3C] rounded-3xl p-8 md:p-12 mb-5 relative" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-37">
          {/* Navigation arrows */}
          <button onClick={goToPrev} className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10" aria-label="Previous accreditations" data-id="element-38">
            <ChevronLeft className="h-5 w-5" data-id="element-39" />
          </button>
          <button onClick={goToNext} className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10" aria-label="Next accreditations" data-id="element-40">
            <ChevronRight className="h-5 w-5" data-id="element-41" />
          </button>

          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 px-6 md:px-10" data-id="element-42">
            {visibleItems.map(item => <div key={item.label} className="flex flex-col items-center text-center" data-id="element-43">
                <div className="bg-white rounded-2xl p-5 md:p-6 w-full aspect-square max-w-[160px] flex items-center justify-center mb-4 shadow-sm" data-id="element-44">
                  <img src={item.logo} alt={item.alt} className="max-h-20 max-w-full object-contain" loading="lazy" data-id="element-45" />
                </div>
                <p className="text-white text-sm font-medium leading-relaxed px-2" data-id="element-46">
                  {item.detail}
                </p>
              </div>)}
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-2.5 mt-8" data-id="element-47">
            {Array.from({
            length: totalPages
          }).map((_, i) => <button key={i} onClick={() => setCurrentPage(i)} className={`w-3 h-3 rounded-full transition-all duration-300 ${i === currentPage ? 'bg-white scale-110' : 'bg-white/30 hover:bg-white/50'}`} aria-label={`Go to page ${i + 1}`} data-id="element-48" />)}
          </div>
        </div>

        {/* Experience highlights */}
        <div className="bg-white rounded-2xl border border-warm-200 shadow-sm overflow-hidden mb-5" data-id="element-49">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-warm-200" data-id="element-50">
            {highlights.map(stat => <div key={stat.label} className="p-5 md:p-6 text-center" data-id="element-51">
                <div className="text-3xl md:text-4xl font-bold text-teal-600 tracking-tight mb-2" data-id="element-52">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-ink-500 leading-snug" data-id="element-53">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </div>

        {/* Additional trust signals */}
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-3" data-id="element-54">
          {['£10m Professional Indemnity Insurance', 'GDPR & Data Protection Compliant', 'CPR Part 35 Compliant Reports', 'Clinical Governance Framework'].map(text => <div key={text} className="flex items-center text-sm font-medium text-ink-700" data-id="element-55">
              <CheckCircle className="h-4 w-4 text-sage-500 mr-2 flex-shrink-0" data-id="element-56" />
              {text}
            </div>)}
        </div>
      </div>
    </section>;
}